<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template #button-content>
                <i class="mc-fa icon-outline_download" />{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">
        {{ $t('report.fields.dataSummary') }}
      </h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="!isLoading" class="mx-1 my-3 mc-dashboard-card">
        <b-col class="border border-1 p-2 d-flex justify-content-center align-items-center">
          <div>
            <p class="text-muted">
              {{ $t('maintenance.summary.totalService') }}
            </p>
            <h3 class="mb-0 text-center">
              {{ (total.DistanceAndTime ?? 0) + (total.Hourmeter ?? 0) }}
            </h3>
          </div>
        </b-col>
        <b-col class="border border-1 p-2">
          <div class="row">
            <div class="col-md-9">
              <p class="text-muted">
                {{ $t('maintenance.summary.inService') }}
              </p>
              <h5 class="font-size-13 mb-0">
                {{
                  (dataheader.distanceAndTime.totalInProgress ?? 0) +
                  (dataheader.hourmeter.totalInProgress ?? 0)
                }}
              </h5>
            </div>
            <div class="avatar-sm">
              <div class="avatar-title rounded-circle bg-info font-size-20">
                <i class="icon-outline_truck-maintenance mc-fa" />
              </div>
            </div>
          </div>
        </b-col>
        <b-col class="border border-1 p-2">
          <div class="row">
            <div class="col-md-9">
              <p class="text-muted">
                {{ $t('maintenance.summary.lateService') }}
              </p>
              <h5 class="font-size-13 mb-0">
                {{
                  (dataheader.distanceAndTime.totalLate ?? 0) +
                  (dataheader.hourmeter.totalLate ?? 0)
                }}
              </h5>
            </div>
            <div class="avatar-sm">
              <div class="avatar-title rounded-circle bg-danger font-size-20">
                <i class="icon-outline_alert-triangle mc-fa" />
              </div>
            </div>
          </div>
        </b-col>
        <b-col class="border border-1 p-2">
          <div class="row">
            <div class="col-md-9">
              <p class="text-muted">
                {{ $t('maintenance.summary.approachingService') }}
              </p>
              <h5 class="font-size-13 mb-0">
                {{
                  (dataheader.distanceAndTime.totalSoon ?? 0) +
                  (dataheader.hourmeter.totalSoon ?? 0)
                }}
              </h5>
            </div>
            <div class="avatar-sm">
              <div class="avatar-title rounded-circle bg-warning font-size-20">
                <i class="icon-outline_timer mc-fa"></i>
              </div>
            </div>
          </div>
        </b-col>
        <b-col class="border border-1 p-2">
          <div class="row">
            <div class="col-md-9">
              <p class="text-muted">
                {{ $t('maintenance.summary.normal') }}
              </p>
              <h5 class="font-size-13 mb-0">
                {{
                  (dataheader.distanceAndTime.totalNormal ?? 0) +
                  (dataheader.hourmeter.totalNormal ?? 0)
                }}
              </h5>
            </div>
            <div class="avatar-sm">
              <div class="avatar-title rounded-circle bg-success font-size-20">
                <i class="icon-fill_checkmark mc-fa" />
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <!-- Kilometer & Waktu -->
    <McGrapqlTable
      v-if="Object.keys(filters).length > 0"
      v-show="tabSelected == '1'"
      bordered
      withCard
      searchable
      :fields="distanceTimeFields"
      :apiClient="kmApiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      reportKey="service-schedules"
      :customButton="true"
      :refreshData="isLoading"
    >
      <template #tab-button>
        <div class="mc-btn--tab">
          <b-form-radio-group
            v-if="showTabFilter"
            id="btn-radio"
            v-model="tabSelected"
            :options="tabOptions"
            button-variant="outline-primary"
            size="md"
            class="radio-tab mb-2"
            name="radio-btn-outline"
            buttons
          />
        </div>
      </template>
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleKmColumnReport"
        />
      </template>
      <template #cell(vehicle)="data">
        {{ data.value.licensePlate ? data.value.licensePlate : '-' }}
      </template>

      <template #cell(status)="data">
        {{ data.value ? setStatus(data.value) : '-' }}
      </template>

      <template #cell(service)="data">
        {{ data.item.service?.name }} ({{ data.item.service?.serviceAction?.action }})
      </template>

      <template #cell(ssgroup)="data">
        {{ data.value ? data.value.name : '-' }}
      </template>

      <template #cell(startServiceAtDistance)="data">
        {{ data.value ? thousandFormat(data.value) : '-' }}
      </template>

      <template #cell(intervalDistance)="data">
        {{ data.value ? thousandFormat(data.value) : '-' }}
      </template>

      <template #[`cell(vehicle.odometer)`]="data">
        {{ data.value ? thousandFormat(data.value) : '-' }}
      </template>

      <template #cell(startServiceAtTime)="data">
        {{ data.value ? momentDateShortMonth(data.value) : '-' }}
      </template>

      <template #cell(intervalTime)="data">
        {{ data.value ? `${data.value} ${checkDateType(data.item.intervalPeriod)}` : '-' }}
      </template>

      <template #[`cell(serviceTimeDifference.readableText)`]="data">
        {{ data.item.serviceTimeDifference.isDatePassed ? '-' : '' }}
        {{
          data.item.serviceTimeDifference.years == 0
            ? ''
            : `${data.item.serviceTimeDifference.years} ${$t('global.time.year')}`
        }}
        {{
          data.item.serviceTimeDifference.months == 0
            ? ''
            : `${data.item.serviceTimeDifference.months} ${$t('global.time.month')}`
        }}
        {{
          data.item.serviceTimeDifference.days == 0
            ? ''
            : `${data.item.serviceTimeDifference.days} ${$t('global.time.days')}`
        }}
      </template>
    </McGrapqlTable>

    <!-- Durasi Jam Kerja -->
    <McGrapqlTable
      v-if="Object.keys(filters).length > 0"
      v-show="tabSelected == '2'"
      bordered
      withCard
      searchable
      :fields="hourmeterFields"
      :apiClient="hourmeterApiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      reportKey="service-schedules"
      :customButton="true"
      :refreshData="isLoading"
    >
      <template #tab-button>
        <div class="mc-btn--tab">
          <b-form-radio-group
            v-if="showTabFilter"
            id="btn-radio"
            v-model="tabSelected"
            :options="tabOptions"
            button-variant="outline-primary"
            size="md"
            class="radio-tab mb-2"
            name="radio-btn-outline"
            buttons
          />
        </div>
      </template>
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleHourmeterColumnReport"
        />
      </template>
      <template #cell(vehicle)="data">
        {{ data.value.licensePlate ? data.value.licensePlate : '-' }}
      </template>

      <template #cell(status)="data">
        {{ data.value ? setStatus(data.value) : '-' }}
      </template>

      <template #cell(service)="data">
        {{ data.item.service?.name }} ({{ data.item.service?.serviceAction?.action }})
      </template>

      <template #cell(ssgroup)="data">
        {{ data.value ? data.value.name : '-' }}
      </template>

      <template #cell(intervalHourmeter)="data">
        {{ data.value ? minuteToHour(data.value) : '-' }}
      </template>

      <template #cell(startServiceAtHourmeter)="data">
        {{ data.value ? minuteToHour(data.value) : '-' }}
      </template>

      <template #cell(totalDurationHourmeter)="data">
        {{ data.item.vehicle ? minuteToHour(data.item.vehicle.hourmeter) : '-' }}
      </template>

      <template #cell(serviceHourmeterDifference)="data">
        {{ data.value ? minuteToHour(data.value) : '-' }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="refreshData"
    />
    <McReportDownloader />
    <ModalSavedReportVerification :domain="domain" />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import { kmFields, hourmeterFields } from './lib/fields/fields'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { GET_SERVICE_SCHEDULES } from '@/services/api/maintenance/serviceSchedules'
import { EXPORT_SERVICE_SCHEDULE } from '@/services/api/report/report'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import cloneDeep from 'lodash/cloneDeep'
import McFilterSelect from '@/components/McFilterSelect'
import responsive from '@/mixin/responsive'

moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      distanceTableKey: 0,
      hourmeterTableKey: 0,
      distanceTable: kmFields.map((item, i) => ({ ...item, order: i + 1 })),
      hourmeterTable: hourmeterFields.map((item, i) => ({ ...item, order: i + 1 })),
      isBusy: false,
      reportKey: 'service-schedules',
      filterDetail: {},
      summaryKey: 50,
      total: {
        DistanceAndTime: 0,
        Hourmeter: 0,
      },
      tabSelected: '1',
      dataheader: {
        distanceAndTime: {},
        hourmeter: {},
      },
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    showTabFilter() {
      const showTabFilter =
        Object.keys(
          Object.fromEntries(Object.entries(this.total).filter(([_, value]) => value > 0))
        ).length > 1
      return showTabFilter
    },
    tabOptions() {
      let tab = []
      if (this.total['Hourmeter'] > 0 && this.total['DistanceAndTime'] > 0) {
        tab = [
          {
            text: `${this.$t('maintenance.form.kmTime')} (${this.total['DistanceAndTime']})`,
            value: '1',
            key: 'DistanceAndTime',
          },
          {
            text: `${this.$t('maintenance.form.hourmeter')} (${this.total['Hourmeter']})`,
            value: '2',
            key: 'Hourmeter',
          },
        ]
      } else if (this.total['Hourmeter'] == 0) {
        tab = [
          {
            text: `${this.$t('maintenance.form.kmTime')} (${this.total['DistanceAndTime']})`,
            value: '1',
            key: 'DistanceAndTime',
          },
        ]
      } else if (this.total['DistanceAndTime'] == 0) {
        tab = [
          {
            text: `${this.$t('maintenance.form.hourmeter')} (${this.total['Hourmeter']})`,
            value: '2',
            key: 'Hourmeter',
          },
        ]
      }
      return tab
    },
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    distanceTimeFields() {
      const fields = cloneDeep(this.distanceTable)
      return fields
    },
    hourmeterFields() {
      const fields = cloneDeep(this.hourmeterTable)
      return fields
    },
    kmColumns() {
      return this.distanceTable
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    hourmeterColumns() {
      return this.hourmeterTable
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, serviceStatus, ...otherFilter } = filters
        const startDate =
          periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
        const endDate =
          periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null
        const model = {
          startServiceAtTime: {
            start: startDate,
            end: endDate,
          },
          status: serviceStatus ? this.statusFilter(serviceStatus) : undefined,
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    kmApiClient() {
      return {
        get: async (filter) => {
          filter.type = 'DistanceAndTime'
          return this.get(filter)
        },
      }
    },
    hourmeterApiClient() {
      return {
        get: async (filter) => {
          filter.type = 'Hourmeter'
          return this.get(filter)
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  methods: {
    setStatus(status) {
      let label = this.$t('maintenance.status.normal')

      if (status.includes('I')) {
        label = this.$t('maintenance.status.inService')
      } else if (status.includes('LO') || status.includes('LD') || status.includes('LH')) {
        label = this.$t('maintenance.status.lateService')
      } else if (status.includes('SO') || status.includes('SD') || status.includes('SH')) {
        label = this.$t('maintenance.status.approachingService')
      }
      return label
    },
    checkDateType(date) {
      switch (date) {
        case 'd':
          return this.$t('global.time.days')
        case 'w':
          return this.$t('global.time.week')
        case 'm':
          return this.$t('global.time.month')
      }
    },
    statusFilter(value) {
      let late = []
      let soon = []
      let normal = []
      let inProgress = []

      let data = []

      value.filter((item) => {
        if (item === 3) {
          late = ['lateDate', 'lateOdometer', 'lateHourmeter']
        } else if (item === 4) {
          soon = ['soonDate', 'soonOdometer', 'soonHourmeter']
        } else if (item === 1) {
          normal = ['normalDate', 'normalOdometer', 'normalHourmeter']
        } else if (item === 2) {
          inProgress = ['inProgress']
        }
      })

      data = data.concat(late, soon, normal, inProgress)

      return data
    },
    async get(filter) {
      const { valid, model } = this.generateFilter
      if (!valid) return {}

      this.isBusy = true
      const res = await this.$apollo.query({
        query: GET_SERVICE_SCHEDULES,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...model,
            ...filter,
          },
        },
      })

      if (!res.data.serviceSchedules.serviceSchedules.data.serviceSchedules.length) {
        if (filter.type == 'Hourmeter') {
          this.dataheader.hourmeter = {
            totalLate: 0,
            totalSoon: 0,
            totalInProgress: 0,
            totalNormal: 0,
          }
        } else {
          this.dataheader.distanceAndTime = {
            totalLate: 0,
            totalSoon: 0,
            totalInProgress: 0,
            totalNormal: 0,
          }
        }

        this.total[filter.type] = 0
        this.isBusy = false
        return {
          data: [],
          pagination: {
            page: 1,
            total: 0,
          },
        }
      }

      const kilometerData = res.data.serviceSchedules.serviceSchedules.data.serviceSchedules.filter(
        (item) => item.type === 'DistanceAndTime'
      )
      if (kilometerData.length == 0) this.tabSelected = '2'
      else this.tabSelected = '1'

      if (filter.type == 'Hourmeter') {
        this.dataheader.hourmeter = {
          totalLate: res.data.serviceSchedules.serviceSchedules.data.totalLate,
          totalSoon: res.data.serviceSchedules.serviceSchedules.data.totalSoon,
          totalInProgress: res.data.serviceSchedules.serviceSchedules.data.totalInProgress,
          totalNormal: res.data.serviceSchedules.serviceSchedules.data.totalNormal,
        }
      } else {
        this.dataheader.distanceAndTime = {
          totalLate: res.data.serviceSchedules.serviceSchedules.data.totalLate,
          totalSoon: res.data.serviceSchedules.serviceSchedules.data.totalSoon,
          totalInProgress: res.data.serviceSchedules.serviceSchedules.data.totalInProgress,
          totalNormal: res.data.serviceSchedules.serviceSchedules.data.totalNormal,
        }
      }

      const { serviceSchedules } = res.data.serviceSchedules.serviceSchedules.data

      serviceSchedules.forEach((row) => {
        if (row.service2Ssgroup) {
          if (row.service2Ssgroup.serviceId == row.serviceId) {
            row.intervalDistance = row.service2Ssgroup.intervalDistance
            row.reminderDistance = row.service2Ssgroup.reminderDistance
            row.intervalHourmeter = row.service2Ssgroup.intervalHourmeter
            row.reminderHourmeter = row.service2Ssgroup.reminderHourmeter
            row.intervalTime = row.service2Ssgroup.intervalTime
            row.intervalPeriod = row.service2Ssgroup.intervalPeriod
            row.reminderTime = row.service2Ssgroup.reminderTime
            row.reminderPeriod = row.service2Ssgroup.reminderPeriod
          }
        }
      })

      const result = {
        data: serviceSchedules,
        pagination: res.data.serviceSchedules.serviceSchedules.pagination,
      }
      this.total[filter.type] = result.pagination.total
      this.isBusy = false

      return result
    },
    handleKmColumnReport(fields) {
      this.distanceTable = fields
    },
    handleHourmeterColumnReport(fields) {
      this.hourmeterTable = fields
    },
    refreshData() {
      this.isLoading = true
      this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      setTimeout(() => {
        this.isLoading = false
      }, 300)
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.serviceSchedulesReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        title: 'SERVICE_SCHEDULE_DETAIL',
        ...model,
        column: this.kmColumns,
        hourmeterColumn: this.hourmeterColumns,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
        useColorOnStatus: false,
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: EXPORT_SERVICE_SCHEDULE,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.serviceSchedulesReport)
        })
    },
  },
}
</script>
